/**
 * Validates Slovak receipt QR codes
 * Supports two formats:
 * 1. Online: {letter}-{32 hex characters} (e.g., O-, V-)
 * 2. Offline: [OKP:]okp_hash:register_id:datetime:sequence:amount (OKP: prefix optional)
 */

export interface QRValidationResult {
  valid: boolean;
  format: 'online' | 'offline' | null;
  uid: string;
}

/**
 * Validates a QR code string from Slovak receipt
 * @param qrData - Raw QR code data
 * @returns Validation result with format and UID
 */
export function validateSKReceiptQR(qrData: string): QRValidationResult {
  if (!qrData || typeof qrData !== 'string') {
    return { valid: false, format: null, uid: '' };
  }

  const trimmedData = qrData.trim();

  // Online format: {letter}-{32 hex chars} (O-, V-, etc.)
  // Example: O-A1B2C3D4E5F6789012345678901234AB or V-0FBCFA8EFDCF4ADCBCFA8EFDCF2ADCCD
  const onlinePattern = /^[A-Z]-[0-9A-F]{32}$/i;
  if (onlinePattern.test(trimmedData)) {
    return { valid: true, format: 'online', uid: trimmedData };
  }

  // Offline format: [OKP:]okp_hash:register:datetime:seq:amount
  // With prefix:    OKP:88812345678:20250110142530:0001:65.37
  // Without prefix: c2a10535-19e9d13c-67725efb-fb729285-287ba976:88821203094550001:260331195424:1321:74.10
  const offlineWithPrefix = /^OKP:[^:]+:\d{12,14}:\d+:[\d.]+$/;
  const offlineWithoutPrefix = /^[0-9a-f-]+:\d{10,}:\d{12,14}:\d+:[\d.]+$/i;
  if (offlineWithPrefix.test(trimmedData) || offlineWithoutPrefix.test(trimmedData)) {
    return { valid: true, format: 'offline', uid: trimmedData };
  }

  return { valid: false, format: null, uid: '' };
}

/**
 * Formats validation error messages in Slovak
 * @param qrData - The invalid QR data
 * @returns User-friendly error message
 */
export function getValidationErrorMessage(qrData: string): string {
  if (!qrData) {
    return 'QR kód je prázdny';
  }

  if (/^[A-Z]-/i.test(qrData)) {
    return 'Neplatný online UID formát. Očakávaný: X-{32 hex znakov}';
  }

  if (qrData.startsWith('OKP:')) {
    return 'Neplatný offline UID formát. Skontrolujte QR kód.';
  }

  return 'Neznámy formát QR kódu. Skúste znova alebo zadajte UID manuálne.';
}
